import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import api from './api';
import TermsOfService from './docs/TermsOfService';
import PrivacyPolicy from './docs/PrivacyPolicy';
import RefundPolicy from './docs/RefundPolicy';

function Register({ onSwitchToLogin }) {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Состояния для сброса пароля
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // Состояние для открытия документов во всплывающем окне
  const [activeDoc, setActiveDoc] = useState(null);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    
    html.style.setProperty('margin', '0', 'important');
    html.style.setProperty('padding', '0', 'important');
    html.style.setProperty('background-color', '#090d16', 'important');
    
    body.style.setProperty('margin', '0', 'important');
    body.style.setProperty('padding', '0', 'important');
    body.style.setProperty('background-color', '#090d16', 'important');

    return () => {
      html.style.backgroundColor = '';
      body.style.backgroundColor = '';
    };
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('register/', {
        username: formData.username,
        email: formData.email,
        password: formData.password,
      });
      if (response.status === 201 || response.status === 200) {
        setSuccess(true);
      }
    } catch (err) {
      const data = err.response?.data;

      if (data) {
        // Проверяем ошибки по конкретным полям от Django
        if (data.username) {
          setError(Array.isArray(data.username) ? data.username[0] : data.username);
        } else if (data.email) {
          setError(Array.isArray(data.email) ? data.email[0] : data.email);
        } else if (data.password) {
          setError(Array.isArray(data.password) ? data.password[0] : data.password);
        } else if (data.detail) {
          setError(data.detail);
        } else if (data.message) {
          setError(data.message);
        } else {
          setError('Ошибка при регистрации. Проверьте введенные данные.');
        }
      } else {
        setError('Не удалось связаться с сервером. Попробуйте позже.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Обработчик отправки письма для сброса пароля
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetMessage('');
    setResetError('');
    setResetLoading(true);

    try {
      const response = await api.post('request-password-reset/', {
        email: resetEmail,
      });
      setResetMessage(response.data?.message || 'Инструкции отправлены на ваш Email!');
    } catch (err) {
      setResetError(err.response?.data?.email?.[0] || err.response?.data?.error || 'Пользователь с таким email не найден.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: '#090d16',
        display: 'flex',
        overflow: 'hidden',
        zIndex: 9999
      }}
      className="text-white"
    >
      {/* SEO Метатеги */}
      <Helmet>
        <title>Регистрация — RealWork.pro</title>
        <meta name="description" content="Создайте аккаунт на платформе RealWork.pro, чтобы нанимать специалистов или брать в работу фриланс-заказы." />
        <meta property="og:title" content="Регистрация аккаунта — RealWork.pro" />
        <meta property="og:description" content="Присоединяйтесь к экосистеме RealWork.pro для безопасной удаленной работы." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* ЛЕВАЯ ЧАСТЬ */}
      <div 
        className="hidden md:flex md:w-1/2 relative flex-col justify-between p-12 lg:p-16 bg-cover bg-center h-full"
        style={{ 
          backgroundImage: `linear-gradient(to bottom, rgba(15, 23, 42, 0.95), rgba(30, 27, 75, 0.95)), url('http://googleusercontent.com/image_collection/image_retrieval/498531985610772507_0')` 
        }}
      >
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-indigo-500 rounded-full blur-[120px] opacity-20"></div>
        <div className="flex items-center space-x-3 z-10">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg shadow-indigo-500/30">
            <span className="font-bold text-xl tracking-wider text-white">R</span>
          </div>
          <span className="font-bold text-2xl tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">
            Realwork<span className="text-indigo-400">.pro</span>
          </span>
        </div>
        <div className="my-auto max-w-lg z-10 py-2">
          <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight mb-4">
            Создайте аккаунт <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">
              в пару кликов
            </span>
          </h1>
        </div>
        <div className="text-gray-500 text-xs z-10">© 2026 Realwork.pro. Экосистема для работы.</div>
      </div>

      {/* ПРАВАЯ ЧАСТЬ */}
      <div className="w-full md:w-1/2 flex flex-col justify-between items-center p-6 bg-[#090d16] h-full overflow-y-auto">
        <div className="hidden md:block h-6"></div>

        <div className="w-full max-w-sm bg-[#111827]/60 p-6 rounded-2xl border border-gray-800 backdrop-blur-md shadow-2xl space-y-4 my-auto">
          <div className="text-center md:text-left">
            <h2 className="text-2xl font-bold tracking-tight">Регистрация</h2>
            <p className="text-gray-400 text-xs mt-0.5">Внесите данные учетной записи</p>
          </div>

          {success ? (
            <div className="text-center py-4 space-y-3">
              <div className="text-xl">🎉</div>
              <p className="text-emerald-400 font-bold text-sm">Успешно зарегистрировано!</p>
              <button
                type="button"
                onClick={onSwitchToLogin}
                className="w-full py-2 bg-indigo-600 rounded-xl text-sm font-bold"
              >
                Войти в аккаунт
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              {error && <div className="p-2 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl text-xs">{error}</div>}
              
              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-gray-400 mb-1">Логин</label>
                <input type="text" name="username" required value={formData.username} onChange={handleChange} className="w-full bg-[#1f2937]/50 border border-gray-700 rounded-xl py-2 px-3 text-white text-sm outline-none" />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-gray-400 mb-1">Email</label>
                <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full bg-[#1f2937]/50 border border-gray-700 rounded-xl py-2 px-3 text-white text-sm outline-none" />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-gray-400 mb-1">Пароль</label>
                <input type="password" name="password" required value={formData.password} onChange={handleChange} className="w-full bg-[#1f2937]/50 border border-gray-700 rounded-xl py-2 px-3 text-white text-sm outline-none" />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-gray-400 mb-1">Подтверждение</label>
                <input type="password" name="confirmPassword" required value={formData.confirmPassword} onChange={handleChange} className="w-full bg-[#1f2937]/50 border border-gray-700 rounded-xl py-2 px-3 text-white text-sm outline-none" />
              </div>

              <button type="submit" disabled={loading} className="w-full py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm mt-2 transition">
                {loading ? 'Регистрация...' : 'Зарегистрироваться'}
              </button>

              <div className="flex flex-col sm:flex-row justify-between items-center gap-2 pt-2 text-xs">
  <button 
    type="button" 
    onClick={onSwitchToLogin} 
    className="text-indigo-400 hover:text-indigo-300 font-medium transition duration-200 hover:underline cursor-pointer"
  >
    Уже есть аккаунт? Войти
  </button>
  
  <button 
    type="button" 
    onClick={() => setShowResetModal(true)} 
    className="text-slate-400 hover:text-white transition duration-200 underline cursor-pointer"
  >
    Забыли пароль?
  </button>
</div>
            </form>
          )}
        </div>

        {/* МИНИ-ФУТЕР С КНОПКАМИ ДЛЯ ОТКРЫТИЯ МОДАЛОК */}
        <div className="w-full max-w-sm text-center pt-6 pb-2 border-t border-slate-800/40 mt-4">
          <div className="flex justify-center gap-3 text-[11px] text-gray-500">
            <button 
              onClick={() => setActiveDoc('terms')} 
              className="hover:text-indigo-400 transition bg-none border-none p-0 cursor-pointer outline-none underline"
            >
              Соглашение
            </button>
            <span>•</span>
            <button 
              onClick={() => setActiveDoc('privacy')} 
              className="hover:text-indigo-400 transition bg-none border-none p-0 cursor-pointer outline-none underline"
            >
              Конфиденциальность
            </button>
            <span>•</span>
            <button 
              onClick={() => setActiveDoc('refund')} 
              className="hover:text-indigo-400 transition bg-none border-none p-0 cursor-pointer outline-none underline"
            >
              Возврат
            </button>
          </div>
          <div className="text-[10px] text-gray-600">
            Поддержка: <a href="mailto:realwork.support@gmail.com" className="text-indigo-400/80 hover:underline hover:text-indigo-300 transition">realwork.support@gmail.com</a>
          </div>
        </div>
      </div>

      {/* --- МОДАЛЬНОЕ ОКНО СБРОСА ПАРОЛЯ --- */}
      {showResetModal && (
        <div 
          onClick={() => { setShowResetModal(false); setResetMessage(''); setResetError(''); }} 
          className="fixed inset-0 bg-black/80 flex justify-center items-center p-4"
          style={{ zIndex: 10001 }}
        >
          <div 
            onClick={(e) => e.stopPropagation()} 
            className="bg-[#111827] border border-gray-800 rounded-2xl p-6 max-w-md w-full relative text-white shadow-2xl space-y-4"
          >
            <button 
              onClick={() => { setShowResetModal(false); setResetMessage(''); setResetError(''); }} 
              className="absolute top-4 right-4 text-gray-400 hover:text-white font-bold text-sm transition"
            >
              ✕
            </button>

            <div>
              <h3 className="text-xl font-bold">Восстановление пароля</h3>
              <p className="text-gray-400 text-xs mt-1">Укажите Email, связанный с вашим аккаунтом. Мы отправим ссылку для сброса.</p>
            </div>

            {resetMessage ? (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl text-xs font-medium text-center">
                {resetMessage}
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-3">
                {resetError && (
                  <div className="p-2 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl text-xs">
                    {resetError}
                  </div>
                )}
                <div>
                  <label className="block text-xs uppercase tracking-wider font-bold text-gray-400 mb-1">Email</label>
                  <input 
                    type="email" 
                    required 
                    value={resetEmail} 
                    onChange={(e) => setResetEmail(e.target.value)} 
                    placeholder="example@gmail.com"
                    className="w-full bg-[#1f2937]/50 border border-gray-700 rounded-xl py-2 px-3 text-white text-sm outline-none"
                  />
                </div>
                <button 
                  type="submit" 
                  disabled={resetLoading}
                  className="w-full py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm transition"
                >
                  {resetLoading ? 'Отправка...' : 'Отправить ссылку'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* --- ВСПЛЫВАЮЩИЕ ОКНА ДЛЯ ДОКУМЕНТОВ --- */}
      {activeDoc && (
        <div 
          onClick={() => setActiveDoc(null)} 
          className="fixed inset-0 bg-black/80 flex justify-center items-center p-4"
          style={{ zIndex: 10000 }}
        >
          <div 
            onClick={(e) => e.stopPropagation()} 
            className="bg-white rounded-2xl p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto relative text-slate-800 shadow-2xl"
          >
            <button 
              onClick={() => setActiveDoc(null)} 
              className="absolute top-4 right-4 bg-slate-200 hover:bg-slate-300 text-slate-800 font-bold py-1 px-3 rounded-lg text-xs transition"
            >
              ✕ Закрыть
            </button>
            <div className="mt-4">
              {activeDoc === 'terms' && <TermsOfService />}
              {activeDoc === 'privacy' && <PrivacyPolicy />}
              {activeDoc === 'refund' && <RefundPolicy />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Register;